import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
	theme: Theme;
	setTheme: (theme: Theme) => void;
}

const DEFAULT_THEME: Theme = 'dark';

const isTheme = (value: string | null): value is Theme => {
	return value === 'light' || value === 'dark' || value === 'system';
};

const ThemeContext = createContext<ThemeContextType>({
	theme: DEFAULT_THEME,
	setTheme: () => {},
});

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

interface ThemeProviderProps {
	children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
	const [theme, setTheme] = useState<Theme>(() => {
		const savedTheme = localStorage.getItem('theme');
		return isTheme(savedTheme) ? savedTheme : DEFAULT_THEME;
	});

	const applyTheme = (_newTheme: Theme) => {
		const root = window.document.documentElement;
		root.classList.remove('light', 'dark');
		root.classList.add('dark');
	};

	useEffect(() => {
		applyTheme(theme);

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = () => {
			if (theme === 'system') {
				applyTheme('system');
			}
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [theme]);

	const handleSetTheme = (newTheme: Theme) => {
		setTheme(newTheme);
		localStorage.setItem('theme', newTheme);
		// Apply theme immediately for instant feedback
		applyTheme(newTheme);
	};

	return (
		<ThemeContext.Provider value={{ theme, setTheme: handleSetTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}
